import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      navigate('/admin/products');
    }
  }, [user, loading, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setError('帳號或密碼錯誤，請重新輸入。');
    } else {
      navigate('/admin/products');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f6f8] flex items-center justify-center">
        <i className="ri-loader-4-line text-3xl text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-9 h-9 bg-[#1a2535] flex items-center justify-center">
              <i className="ri-settings-3-line text-white text-lg" />
            </div>
            <span className="text-xl font-bold text-[#1a2535] tracking-wide">益維後台</span>
          </div>
          <p className="text-sm text-gray-400">請登入管理員帳號以繼續</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-100 p-8">
          <h2 className="text-base font-semibold text-gray-700 mb-6">管理員登入</h2>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">電子信箱</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-400 text-sm" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="admin@example.com"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">密碼</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-gray-400 text-sm" />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="請輸入密碼"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5] transition-colors"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 px-3 py-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm" />
                </div>
                <p className="text-xs text-red-500">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1a2535] text-white text-sm py-2.5 hover:bg-[#243347] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap mt-2"
            >
              {submitting ? '登入中...' : '登入後台'}
            </button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-5 leading-relaxed">
            請使用 Supabase Auth 建立管理員帳號<br />
            前往 Authentication &gt; Users 新增使用者
          </p>
        </div>

        <p className="text-center mt-4 text-xs text-gray-400">
          <a href="/" className="hover:text-gray-600 transition-colors cursor-pointer">← 返回官方網站</a>
        </p>
      </div>
    </div>
  );
}
